import React, { useCallback } from 'react';
import { Button, Message } from 'rsuite';
import {
  PageDataTable,
  MobileCard,
  MobileCardList,
} from '@urbantxt/table';
import { MOBILE_TABLE_BREAKPOINT } from 'constants/breakpoints';
import { DataTableColumn } from 'types';
import type { BagelDocumentStatus } from '../../../types';
import useBagelDocumentsTab from '../../hooks/useBagelDocumentsTab';
import { DEFAULT_DOCUMENT_FILTERS } from './documentTableFilters';
import BagelDocumentUploadModal from './BagelDocumentUploadModal';
import BagelDocumentEditMetadataModal from './BagelDocumentEditMetadataModal';
import BagelDocumentProcessingSummary from './BagelDocumentProcessingSummary';
import './BagelDocumentStatusCell';

const COLUMNS: DataTableColumn[] = [
  {
    label: 'Name',
    key: 'name',
    cell: 'text',
    sortable: true,
    width: 200,
    flexGrow: 2,
    fixed: true,
  },
  {
    label: 'Description',
    key: 'description',
    cell: 'tooltip',
    sortable: false,
    width: 300,
    flexGrow: 3,
  },
  {
    label: 'Source file',
    key: 'sourceFileLabel',
    cell: 'text',
    sortable: true,
    width: 180,
    flexGrow: 1,
  },
  {
    label: 'Category',
    key: 'category',
    cell: 'text',
    sortable: true,
    width: 110,
    flexGrow: 0,
  },
  {
    label: 'Doc type',
    key: 'docTypeLabel',
    cell: 'text',
    sortable: true,
    width: 140,
    flexGrow: 1,
  },
  {
    label: 'Source',
    key: 'ingestSourceLabel',
    cell: 'text',
    sortable: true,
    width: 120,
    flexGrow: 0,
  },
  {
    label: 'Year',
    key: 'ingestYear',
    cell: 'text',
    sortable: true,
    width: 70,
    flexGrow: 0,
  },
  {
    label: 'Type',
    key: 'fileType',
    cell: 'text',
    sortable: true,
    width: 70,
    flexGrow: 0,
  },
  {
    label: 'Status',
    key: 'status',
    cell: 'bagelDocumentStatus',
    sortable: true,
    width: 110,
    flexGrow: 0,
  },
  {
    label: 'Error',
    key: 'errorMessage',
    cell: 'tooltip',
    sortable: false,
    width: 220,
    flexGrow: 2,
  },
  {
    label: 'Chunks',
    key: 'chunkCount',
    cell: 'text',
    sortable: true,
    width: 80,
    flexGrow: 0,
  },
];

export default function DocumentsTab({
  readOnly = false,
}: {
  readOnly?: boolean;
}) {
  const {
    page,
    limit,
    sortColumn,
    sortType,
    onPageChange,
    onChangeLimit,
    onSortColumn,
    debouncedSearch,
    clearAllFilters,
    tableRows,
    totalDocs,
    loading,
    error,
    uploadOpen,
    setUploadOpen,
    editingDoc,
    setEditingDoc,
    bulkReprocessing,
    tableFilters,
    handleReprocess,
    handleBulkReprocessYear,
    menuColumn,
    filters,
    search,
    setFilter,
    statusSummary,
    load,
    handleUploaded,
    handleUpdated,
  } = useBagelDocumentsTab({ readOnly });

  const handleStatusChipClick = useCallback(
    (status: BagelDocumentStatus | 'all') => {
      setFilter('status', status);
    },
    [setFilter],
  );

  const hasActiveFilters =
    search.trim() !== '' ||
    filters.ingestSource !== DEFAULT_DOCUMENT_FILTERS.ingestSource ||
    filters.domain !== DEFAULT_DOCUMENT_FILTERS.domain ||
    filters.category !== DEFAULT_DOCUMENT_FILTERS.category ||
    filters.status !== DEFAULT_DOCUMENT_FILTERS.status ||
    filters.year !== DEFAULT_DOCUMENT_FILTERS.year ||
    filters.fileType !== DEFAULT_DOCUMENT_FILTERS.fileType;

  return (
    <div style={{ padding: '16px 0' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h5 style={{ margin: 0 }}>Knowledge Base Documents</h5>
        {!readOnly ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {filters.year !== 'all' ? (
              <Button
                appearance="default"
                size="sm"
                loading={bulkReprocessing}
                onClick={handleBulkReprocessYear}
              >
                Re-process {filters.year}
              </Button>
            ) : null}
            <Button
              appearance="primary"
              size="sm"
              onClick={() => setUploadOpen(true)}
            >
              Upload Document
            </Button>
          </div>
        ) : null}
      </div>

      {error && (
        <Message type="error" showIcon style={{ marginBottom: 12 }}>
          {error}{' '}
          <Button appearance="link" onClick={() => load()}>
            Retry
          </Button>
        </Message>
      )}

      {statusSummary ? (
        <BagelDocumentProcessingSummary
          summary={statusSummary}
          activeStatus={filters.status}
          onStatusClick={handleStatusChipClick}
        />
      ) : null}

      <PageDataTable
        data={tableRows}
        columns={COLUMNS}
        isLoading={loading}
        total={totalDocs}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
        onChangeLimit={onChangeLimit}
        defaultColumns={[]}
        columnKeys={[]}
        onColumnKeysChange={() => {}}
        hideColumnPicker
        search={{
          label: 'Search documents',
          value: search,
          onSearch: debouncedSearch,
        }}
        filters={tableFilters}
        clearAllFilters={clearAllFilters}
        menuColumn={menuColumn}
        pinLeadingActionColumns
        mobileBreakpoint={MOBILE_TABLE_BREAKPOINT}
        mobileContent={
          <MobileCardList
            items={tableRows}
            isLoading={loading}
            page={page}
            limit={limit}
            totalCount={totalDocs}
            onPageChange={onPageChange}
            onChangeLimit={onChangeLimit}
            emptyMessage="No documents match your filters."
            renderCard={(row: (typeof tableRows)[number]) => (
              <MobileCard
                id={row._id}
                name={row.name}
                meta={row.sourceFileLabel}
                details={[
                  { label: 'Category', value: row.category || '—' },
                  {
                    label: 'Type',
                    value: row.docTypeLabel || row.fileType || '—',
                  },
                  { label: 'Year', value: row.ingestYear ?? '—' },
                  { label: 'Status', value: row.status || '—' },
                  { label: 'Chunks', value: String(row.chunkCount ?? 0) },
                ]}
                menuItems={menuColumn?.menuItemsGenerator?.(row)}
                ariaLabel={row.name}
              />
            )}
          />
        }
      />

      {totalDocs === 0 && !loading && !hasActiveFilters && (
        <Message type="info" showIcon style={{ marginTop: 12 }}>
          No documents yet. Upload a PDF, DOCX, TXT, or MD file to build
          the knowledge base.
        </Message>
      )}

      {totalDocs === 0 && !loading && hasActiveFilters && (
        <Message type="info" showIcon style={{ marginTop: 12 }}>
          No documents match your search or filters.
        </Message>
      )}

      {uploadOpen && (
        <BagelDocumentUploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={handleUploaded}
        />
      )}

      {editingDoc && (
        <BagelDocumentEditMetadataModal
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onUpdated={handleUpdated}
          onReprocess={handleReprocess}
        />
      )}
    </div>
  );
}
